import { useCallback, useEffect, useState } from "react";
import { searchAddress } from "../services/geocoding-service";
import type { NominatimResult, UseLocationSearchOptions } from "../types";
import { useDebounce } from "./use-debounce";

export const useLocationSearch = (options: UseLocationSearchOptions = {}) => {
	const { debounceMs = 500, limit = 5 } = options;

	const [query, setQuery] = useState("");
	const [results, setResults] = useState<NominatimResult[]>([]);
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [isOpen, setIsOpen] = useState(false);

	const debouncedQuery = useDebounce(query, debounceMs);

	const handleInputChange = useCallback((value: string) => {
		setQuery(value);
		setIsOpen(value.length > 0);
	}, []);

	const clearSearch = useCallback(() => {
		setQuery("");
		setResults([]);
		setError(null);
		setIsOpen(false);
	}, []);

	const closeResults = useCallback(() => {
		setIsOpen(false);
	}, []);

	useEffect(() => {
		const fetchResults = async () => {
			if (debouncedQuery.length < 3) {
				setResults([]);
				return;
			}

			setIsLoading(true);
			setError(null);

			try {
				const data = await searchAddress(debouncedQuery, limit);
				setResults(data);
				setIsOpen(true);
			} catch {
				setError("Erro ao buscar endereço");
				setResults([]);
			} finally {
				setIsLoading(false);
			}
		};

		fetchResults();
	}, [debouncedQuery, limit]);

	return {
		query,
		results,
		isLoading,
		error,
		isOpen,
		handleInputChange,
		clearSearch,
		closeResults,
	};
};
